import { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Collapse, Divider, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useFieldSetter } from "src/utils/useFieldSetter";
import { captureError } from "src/utils/errorReporting";
import SpecRow from "./SpecRow";
import SectionHeader from "./SectionHeader";
import { useT } from "src/i18n";

const PRIVATE_SENTINEL = "private";
const CREATE_SENTINEL = "__create__";
const SENTINEL_VALUES = new Set(["", PRIVATE_SENTINEL, CREATE_SENTINEL]);
const Y_TUNNUS_REGEX = /^\d{7}-\d$/;

function isRealSelection(value) {
    return !!value && !SENTINEL_VALUES.has(String(value));
}

function formatAsiakas(a, t) {
    if (!a) return "";
    const type = a.asiakasTypeId === 1 ? t("wizard.laskutusSection.typeCompany") : t("wizard.laskutusSection.typePrivate");
    const y = a.ytunnus ? ` (${a.ytunnus})` : "";
    return `${a.asiakasNimi}${type}${y}`;
}

/**
 * Laskutus picker for Tarkista (wizard step 5).
 * Spec: docs/superpowers/specs/2026-05-13-betonijerry-laskutus-step-design.md
 *
 * Props:
 *   asiakases          — list from GET /api/me/asiakases
 *   selectedAsiakasId  — string | "private" | null
 *   onSelectAsiakas    — (id) => void
 *   onCreateCompany    — async ({ ytunnus, asiakasNimi }) => { asiakasId } | { alreadyExisted, asiakasId }
 *   onEditSelected     — (asiakasId) => void, opens per-asiakas edit dialog (Muokkaa)
 *   onEditingChange    — (boolean) => void, reports view/edit + create-panel state so the
 *                        orchestrator can gate Lähetä via canAdvance
 *   person             — { personFirstName, personLastName } for the "Laskuta yksityishenkilönä" label
 *   errors             — { laskutus?: string }
 */
export default function LaskutusSection({
    asiakases,
    selectedAsiakasId,
    onSelectAsiakas,
    onCreateCompany,
    onEditSelected,
    onEditingChange,
    person,
    errors,
}) {
    const t = useT();
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ ytunnus: "", asiakasNimi: "" });
    const [createErrors, setCreateErrors] = useState({});
    const [creating, setCreating] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [infoMsg, setInfoMsg] = useState(null);

    // Default to "view" when a real asiakas is already selected (the common
    // case after defaultAsiakasFromList auto-picks one). Without a selection,
    // start in "edit" so the dropdown is immediately accessible.
    const [mode, setMode] = useState(() =>
        isRealSelection(selectedAsiakasId) ? "view" : "edit"
    );

    const fullName = person?.fullName ?? "";

    const value = selectedAsiakasId ?? "";

    const selectedAsiakas = useMemo(
        () => asiakases.find((a) => String(a.asiakasId) === String(value)) || null,
        [asiakases, value],
    );

    // Edge case: the parent's defaultAsiakasFromList may auto-pick AFTER this
    // section mounts (slow asiakases fetch). Flip to "view" once the first
    // real value arrives. One-shot via ref so that a later asiakases refetch
    // (e.g. after edit-dialog save) doesn't undo a user-clicked "Vaihda".
    const hasAutoFlippedRef = useRef(isRealSelection(selectedAsiakasId));
    useEffect(() => {
        if (!hasAutoFlippedRef.current && isRealSelection(value) && selectedAsiakas) {
            hasAutoFlippedRef.current = true;
            setMode("view");
        }
    }, [value, selectedAsiakas]);

    // Tell the parent whether the section is mid-edit (dropdown or create panel
    // visible) so the "Lähetä tarjouspyyntö" button can be disabled until the
    // user commits to a selection. Cleanup resets to false so an unmount
    // (user navigates away from step 5 mid-edit) doesn't leave the parent
    // with stale `laskutusEditing === true` blocking the send button after
    // they come back.
    useEffect(() => {
        onEditingChange?.(mode === "edit" || createOpen);
        return () => onEditingChange?.(false);
    }, [mode, createOpen, onEditingChange]);

    // "Laskuta yksityishenkilönä" is a find-or-create shortcut for the user's
    // own typeId=2 asiakas. When one already exists in the list, the shortcut
    // would resolve to that same row — surface only the real row to avoid
    // showing two functionally identical options.
    const hasPrivateAsiakas = asiakases.some((a) => a.asiakasTypeId === 2);

    function handleSelect(e) {
        const v = e.target.value;
        if (v === CREATE_SENTINEL) {
            setInfoMsg(null);
            setCreateOpen(true);
            return;
        }
        if (createOpen) setCreateOpen(false);
        onSelectAsiakas(v || null);
        // Picking a concrete value commits the choice — collapse back to view.
        // PRIVATE_SENTINEL still counts: the parent resolves it to a real id async.
        if (isRealSelection(v) || v === PRIVATE_SENTINEL) setMode("view");
    }

    const updateCreate = useFieldSetter(setCreateForm, createErrors, setCreateErrors);

    function validateCreate(form) {
        const errs = {};
        if (!Y_TUNNUS_REGEX.test(form.ytunnus.trim())) {
            errs.ytunnus = t("wizard.laskutusSection.ytunnusInvalid");
        }
        const nimi = form.asiakasNimi.trim();
        if (!nimi) errs.asiakasNimi = t("wizard.laskutusSection.nameRequired");
        else if (nimi.length > 250) errs.asiakasNimi = t("wizard.laskutusSection.nameTooLong");
        return errs;
    }

    async function submitCreate() {
        const errs = validateCreate(createForm);
        if (Object.keys(errs).length > 0) {
            setCreateErrors(errs);
            return;
        }
        setCreating(true);
        setSubmitError(null);
        try {
            const result = await onCreateCompany({
                ytunnus: createForm.ytunnus.trim(),
                asiakasNimi: createForm.asiakasNimi.trim(),
            });
            // Success — parent has refetched asiakases and selected the new id.
            setCreateOpen(false);
            setCreateForm({ ytunnus: "", asiakasNimi: "" });
            setCreateErrors({});
            setMode("view");
            if (result?.alreadyExisted) {
                setInfoMsg(t("wizard.laskutusSection.alreadyExists"));
                setTimeout(() => setInfoMsg(null), 6000);
            }
        } catch (err) {
            captureError(err, { tags: { entity: "asiakas", operation: "create" } });
            setSubmitError(err?.message || t("wizard.laskutusSection.createFailed"));
        } finally {
            setCreating(false);
        }
    }

    function cancelCreate() {
        setCreateOpen(false);
        setCreateForm({ ytunnus: "", asiakasNimi: "" });
        setCreateErrors({});
        setSubmitError(null);
        setInfoMsg(null);
    }

    // Show Muokkaa only when the dropdown points at a real, hydrated row.
    // `selectedAsiakas` is null for sentinels and for ids that no longer
    // exist in the list (e.g. server-side deletion), which would otherwise
    // surface a button that no-ops silently.
    const canEditSelected = !!onEditSelected && !!selectedAsiakas;
    const isView = mode === "view" && isRealSelection(value) && selectedAsiakas;

    return (
        <Box>
            <SectionHeader
                title={t("wizard.laskutusSection.title")}
                editLabel={isView ? t("wizard.laskutusSection.switchLabel") : t("wizard.sectionHeader.defaultEditLabel")}
                onEdit={
                    isView ? () => setMode("edit")
                        : canEditSelected ? () => onEditSelected(Number(value))
                            : undefined
                }
            />

            {isView ? (
                <SpecRow label={t("wizard.laskutusSection.billedToLabel")} value={formatAsiakas(selectedAsiakas, t)} />
            ) : (
                <TextField
                    label={t("wizard.laskutusSection.selectLabel")}
                    select
                    fullWidth
                    required
                    value={value}
                    onChange={handleSelect}
                    error={!!errors?.laskutus}
                    helperText={
                        errors?.laskutus
                            ? t(errors.laskutus)
                            : t("wizard.laskutusSection.selectHelper")
                    }
                >
                    {asiakases.map((a) => (
                        <MenuItem key={a.asiakasId} value={String(a.asiakasId)}>
                            {formatAsiakas(a, t)}
                        </MenuItem>
                    ))}
                    <Divider component="li" />
                    {!hasPrivateAsiakas && (
                        <MenuItem value={PRIVATE_SENTINEL}>
                            {t("wizard.laskutusSection.privateOption", { name: fullName ? ` (${fullName})` : "" })}
                        </MenuItem>
                    )}
                    <MenuItem value={CREATE_SENTINEL}>{t("wizard.laskutusSection.createNew")}</MenuItem>
                </TextField>
            )}

            {infoMsg && (
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "info.main" }}>
                    {infoMsg}
                </Typography>
            )}

            <Collapse in={createOpen} unmountOnExit>
                <Stack spacing={1.5} sx={{ pt: 2 }}>
                    <TextField
                        label={t("wizard.laskutusSection.ytunnusLabel")}
                        placeholder="1234567-8"
                        fullWidth
                        value={createForm.ytunnus}
                        onChange={updateCreate("ytunnus")}
                        error={!!createErrors.ytunnus}
                        helperText={createErrors.ytunnus || " "}
                    />
                    <TextField
                        label={t("wizard.laskutusSection.companyNameLabel")}
                        fullWidth
                        value={createForm.asiakasNimi}
                        onChange={updateCreate("asiakasNimi")}
                        error={!!createErrors.asiakasNimi}
                        helperText={createErrors.asiakasNimi || " "}
                    />
                    {submitError && (
                        <Typography variant="caption" color="error">{submitError}</Typography>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            onClick={submitCreate}
                            disabled={creating}
                        >
                            {creating ? t("wizard.laskutusSection.saving") : t("wizard.laskutusSection.save")}
                        </Button>
                        <Button onClick={cancelCreate} disabled={creating}>{t("wizard.shared.cancel")}</Button>
                    </Stack>
                </Stack>
            </Collapse>
        </Box>
    );
}

export const LASKUTUS_PRIVATE_SENTINEL = PRIVATE_SENTINEL;
